import { useEffect, useMemo, useRef, useState } from 'react';

import type { RankingOption } from '@/types/rankingOptionType';
import type { ReviewQuestion, ReviewQuestionOption } from '@/types/review/ReviewQuestion';
import type { DraggableItem } from '@amelio/ui-library';

import RankingOptionList from '@/components/RankingOptionList/RankingOptionList';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { getTextByLanguage } from '@/utils/localization';

const TRANSLATION_KEYS = {
    TITLE: 'evaluation360Questionnaire.questionForm.questions.ranking.longTitle',
    DESCRIPTION: 'evaluation360Questionnaire.questionForm.questions.ranking.description',
    OPTION_LABEL: 'evaluation360Questionnaire.questionForm.questions.ranking.optionLabel',
} as const;

const DEFAULT_TEST_ID_PREFIX = 'ranking-option';
const DEFAULT_GAP = 18;

interface RankingQuestionProps {
    question: ReviewQuestion;
    setQuestion: (question: ReviewQuestion) => void;
    dataTestId?: string;
}

const RankingQuestion = ({ question, setQuestion, dataTestId }: RankingQuestionProps) => {
    const [hasOtherOption, setHasOtherOption] = useState<boolean>(question.hasOtherOption ?? false);
    const defaultLanguage = useSelectedAppLanguage();
    const isMounted = useRef(false);

    const options = question.reviewQuestionOptions ?? [];

    const draggableOptions = useMemo(
        () =>
            options.map((option) => ({
                id: option.id,
                data: { label: getTextByLanguage(option.label, defaultLanguage) },
            })),
        [options],
    );

    useEffect(() => {
        if (options.length !== 0) {
            return;
        }

        if (isMounted.current) {
            return;
        }

        isMounted.current = true;

        setQuestion({
            ...question,
            reviewQuestionOptions: [
                {
                    id: crypto.randomUUID(),
                    label: { [defaultLanguage]: '' },
                    sortOrder: 0,
                    scoreValue: 0,
                },
            ],
        });
    }, [options.length, question, setQuestion, defaultLanguage]);

    const setOptions = (draggableItems: DraggableItem<RankingOption>[]) => {
        const questionOptionItems = draggableItems.map((item, index) => {
            const existingOption = options.find((opt) => opt.id === item.id);
            return {
                id: item.id,
                label: { ...existingOption?.label, [defaultLanguage]: item.data.label },
                sortOrder: index,
                scoreValue: existingOption?.scoreValue ?? 0,
                reviewQuestionId: existingOption?.reviewQuestionId,
            };
        });
        setQuestion({
            ...question,
            reviewQuestionOptions: questionOptionItems,
        });
    };

    const updateOption = (id: string, value: string) => {
        const existingOption = options.find((opt) => opt.id === id);

        if (!existingOption) {
            const newOption: ReviewQuestionOption = {
                id,
                label: { [defaultLanguage]: value },
                sortOrder: options.length,
                scoreValue: 0,
            };
            setQuestion({
                ...question,
                reviewQuestionOptions: [...options, newOption],
            });
            return;
        }

        const updated = options.map((option) => {
            if (option.id === id) {
                return {
                    id: option.id,
                    label: { ...option.label, [defaultLanguage]: value },
                    sortOrder: option.sortOrder,
                    scoreValue: option.scoreValue,
                    reviewQuestionId: option.reviewQuestionId,
                };
            }
            return option;
        });
        setQuestion({
            ...question,
            reviewQuestionOptions: updated,
        });
    };

    const deleteOption = (id: string) => {
        if (options.length === 1 && options[0].id === id) {
            const [lastOption] = options;
            setQuestion({
                ...question,
                reviewQuestionOptions: [
                    {
                        ...lastOption,
                        label: { ...lastOption.label, [defaultLanguage]: '' },
                        sortOrder: 0,
                    },
                ],
            });
            return;
        }

        const updated = options.filter((option) => option.id !== id);
        setQuestion({
            ...question,
            reviewQuestionOptions: updated.map((option, index) => ({
                ...option,
                sortOrder: index,
            })),
        });
    };

    const addOption = () => {
        const newOption: ReviewQuestionOption = {
            id: crypto.randomUUID(),
            label: { [defaultLanguage]: '' },
            sortOrder: options.length,
            scoreValue: 0,
        };

        setQuestion({
            ...question,
            reviewQuestionOptions: [...options, newOption],
        });
    };

    const handleOtherChange = () => {
        const newValue = !hasOtherOption;
        setHasOtherOption(newValue);
        setQuestion({
            ...question,
            hasOtherOption: newValue,
        });
    };

    return (
        <RankingOptionList
            options={draggableOptions}
            setOptions={setOptions}
            updateOption={updateOption}
            deleteOption={deleteOption}
            addOption={addOption}
            handleOtherChange={handleOtherChange}
            isOtherOption={hasOtherOption}
            titleKey={TRANSLATION_KEYS.TITLE}
            descriptionKey={TRANSLATION_KEYS.DESCRIPTION}
            dataTestIdPrefix={DEFAULT_TEST_ID_PREFIX}
            showOtherOption={false}
            showOptionLabel={true}
            optionLabelKey={TRANSLATION_KEYS.OPTION_LABEL}
            gap={DEFAULT_GAP}
            dataTestId={dataTestId}
        />
    );
};

export { RankingQuestion };
